import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
} from "@mui/material";
import {
  IKit,
  ISelectOption,
  SITUACAO_OPTIONS,
  STATUS_OPTIONS,
  TIPO_KIT_OPTIONS,
} from "../types/kit.types";

// Estilos customizados específicos para esta página - SEM BORDA
const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f0f0f0",
    borderRadius: 0,
    minHeight: 56,
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#3C90AD",
    fontWeight: 500,
    fontSize: "0.8rem",
    "&.Mui-focused": {
      color: "#3C90AD",
    },
  },
  "& .MuiInputBase-input": {
    padding: "20px 14px 8px 14px",
  },
};

const selectSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  minHeight: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "20px 14px 8px 14px",
  },
};

const labelSx = {
  color: "#3C90AD",
  fontWeight: 500,
  fontSize: "0.8rem",
  "&.Mui-focused": {
    color: "#3C90AD",
  },
};

interface TabGeralProps {
  kit: Partial<IKit>;
  onChange: (updates: Partial<IKit>) => void;
  referencias: ISelectOption[];
  linhas: ISelectOption[];
  grupos: ISelectOption[];
  tiposKit: ISelectOption[];
  onOpenMidias: () => void;
  // FASE 05 — 4ª validação/Frente 9 2026-07-30: alerta de código já existente exibido
  // no próprio campo quando o usuário sai dele (mesmo padrão de Perfil/Componente).
  codigoDuplicado?: boolean;
}

export const TabGeral: React.FC<TabGeralProps> = ({
  kit,
  onChange,
  referencias,
  linhas,
  grupos,
  tiposKit,
  onOpenMidias,
  codigoDuplicado = false,
}) => {
  const handleChange = (campo: keyof IKit, valor: any) => {
    onChange({ [campo]: valor });
  };

  // FASE 05 — 4ª validação/Frente 9 2026-07-30: formulário reordenado conforme o mockup
  // do PO (image14): mídias à esquerda no padrão dos demais cadastros; Situação no topo
  // direito; linhas Código|Código interno|Status, Descrição, Referência|Linha, Grupo|Tipo.
  // Decisão P2 do PO (30/07): Referência e Linha são obrigatórios — asterisco em todos
  // os campos obrigatórios.
  return (
    <Box>
      {/* FASE 05 — 6ª validação/PO 2026-08-11: Situação numa linha ACIMA do container principal,
          para que Código | Código interno | Status fiquem alinhados ao topo do quadro da imagem. */}
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth required>
            <InputLabel sx={labelSx}>Situação</InputLabel>
            <Select
              value={kit.idSituacao || 1}
              label="Situação"
              disabled
              sx={selectSx}
            >
              {SITUACAO_OPTIONS.map((opcao) => (
                <MenuItem key={opcao.id} value={opcao.id}>
                  {opcao.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {/* Container principal com Imagem à esquerda e campos à direita */}
      <Grid container spacing={3}>
        {/* Coluna Esquerda - Imagem do produto (padrão Componente/Perfil) */}
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff",
              p: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#333", mb: 2, fontWeight: 500 }}
            >
              Imagem do produto
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 180,
                backgroundColor: "#fff",
              }}
            >
              {kit.imagemUrl ? (
                <img
                  src={kit.imagemUrl}
                  alt="Imagem do produto"
                  style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma imagem
                </Typography>
              )}
            </Box>

            {kit.imagemNomeArquivo && (
              <Typography
                variant="caption"
                sx={{ color: "#999", textAlign: "center", display: "block", mt: 2 }}
              >
                {kit.imagemNomeArquivo}
              </Typography>
            )}
          </Box>

          {/* Botão FORA do container da imagem */}
          <Button
            variant="contained"
            fullWidth
            onClick={onOpenMidias}
            sx={{
              backgroundColor: "#3C90AD",
              "&:hover": { backgroundColor: "#2d6f87" },
              textTransform: "none",
              py: 1.2,
            }}
          >
            GERENCIAMENTO DE MÍDIAS
          </Button>
        </Grid>

        {/* Coluna Direita - Campos do Formulário */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            {/* Código */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                required
                label="Código"
                value={kit.codigo || ""}
                onChange={(e) => handleChange("codigo", e.target.value)}
                inputProps={{ maxLength: 50 }}
                error={codigoDuplicado}
                helperText={codigoDuplicado ? "Código já existe" : undefined}
                sx={inputSx}
              />
            </Grid>

            {/* Código interno */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código interno"
                value={kit.codigoInterno || ""}
                onChange={(e) => handleChange("codigoInterno", e.target.value)}
                inputProps={{ maxLength: 50 }}
                sx={inputSx}
              />
            </Grid>

            {/* FASE 05 — 6ª validação/PO 2026-08-07 (correção de rumo): Status na 3ª coluna,
                alinhado com Código/Código interno (Situação fica na linha acima). */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Status</InputLabel>
                <Select
                  value={kit.idStatus || 1}
                  label="Status"
                  onChange={(e) => handleChange("idStatus", e.target.value)}
                  sx={selectSx}
                >
                  {STATUS_OPTIONS.map((opcao) => (
                    <MenuItem key={opcao.id} value={opcao.id}>
                      {opcao.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Descrição */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Descrição"
                value={kit.descricao || ""}
                onChange={(e) => handleChange("descricao", e.target.value)}
                inputProps={{ maxLength: 200 }}
                sx={inputSx}
              />
            </Grid>

            {/* Referência — obrigatória (decisão P2 do PO, 30/07) */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Referência (fornecedor do material)</InputLabel>
                <Select
                  value={kit.idReferencia || ""}
                  onChange={(e) => {
                    const valor = e.target.value;
                    handleChange("idReferencia", valor === "" ? undefined : (typeof valor === "string" ? parseInt(valor) : valor));
                  }}
                  label="Referência (fornecedor do material)"
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {referencias.map((ref) => (
                    <MenuItem key={ref.id} value={ref.id}>
                      {ref.nome || ref.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Linha */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Linha</InputLabel>
                <Select
                  value={kit.idLinha || ""}
                  onChange={(e) => {
                    const valor = e.target.value;
                    handleChange("idLinha", valor === "" ? undefined : (typeof valor === "string" ? parseInt(valor) : valor));
                  }}
                  label="Linha"
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {linhas.map((linha) => (
                    <MenuItem key={linha.id} value={linha.id}>
                      {linha.nome || linha.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Grupo */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Grupo</InputLabel>
                <Select
                  value={kit.idGrupo || ""}
                  onChange={(e) => {
                    const valor = e.target.value;
                    handleChange("idGrupo", valor === "" ? undefined : (typeof valor === "string" ? parseInt(valor) : valor));
                  }}
                  label="Grupo"
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {grupos.map((grupo) => (
                    <MenuItem key={grupo.id} value={grupo.id}>
                      {grupo.nome || grupo.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Tipo */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Tipo</InputLabel>
                <Select
                  value={kit.tipo || ""}
                  onChange={(e) => {
                    const tipoSelecionado = e.target.value;
                    handleChange("tipo", tipoSelecionado);
                    // Limpar medidas se mudar para Ferragem
                    if (tipoSelecionado === "Ferragem") {
                      handleChange("medidas", []);
                    }
                  }}
                  label="Tipo"
                  sx={selectSx}
                >
                  {TIPO_KIT_OPTIONS.map((opcao) => (
                    <MenuItem key={opcao.id} value={opcao.value}>
                      {opcao.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
